import { type ComponentProps, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { toast, type ExternalToast } from 'sonner';

import { cn } from '@/util/utils';

type NoticeProps = Omit<ComponentProps<'div'>, 'children'> &
    VariantProps<typeof noticeVariants> & {
        icon?: ReactNode;
        children: ReactNode;
        /** Shown as a small X when the person may put the notice away. */
        onDismiss?: () => void;
    };

/**
 * A cursory, inline remark: one line on the recessed surface, an icon, an optional way to
 * dismiss it. Lighter than Alert, which reports the state of the form it sits in; a Notice
 * only says something worth knowing on the way past ("You were sent here to log in first").
 */
function Notice({ icon, children, onDismiss, tone, className, ...props }: NoticeProps) {
    return (
        <div
            data-slot="notice"
            role="status"
            className={cn(noticeVariants({ tone }), className)}
            {...props}
        >
            {icon ? (
                <span aria-hidden className="mt-0.5 flex shrink-0 [&_svg]:size-4">
                    {icon}
                </span>
            ) : null}
            <div className="min-w-0 flex-1 text-sm leading-5">{children}</div>
            {onDismiss ? (
                <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={onDismiss}
                    className="focus-visible:outline-ring -m-1 flex size-7 shrink-0 items-center justify-center rounded-sm opacity-70 outline-none hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid [&_svg]:size-3.5"
                >
                    <X />
                </button>
            ) : null}
        </div>
    );
}

const noticeVariants = cva('flex items-start gap-2 rounded-md px-3 py-2', {
    variants: {
        tone: {
            default: 'bg-muted text-foreground [&_svg]:text-muted-foreground',
            success: 'bg-success-muted text-success',
            warning: 'bg-warning-muted text-warning',
        },
    },
    defaultVariants: { tone: 'default' },
});

/**
 * The transient version of a notice: a toast in the corner for something that happened and
 * needs no answer ("You're logged out"). Same voice as Notice, same tokens (see Toaster).
 */
const notify = {
    info: (title: string, options?: ExternalToast) => toast(title, options),
    success: (title: string, options?: ExternalToast) => toast.success(title, options),
    warning: (title: string, options?: ExternalToast) => toast.warning(title, options),
    error: (title: string, options?: ExternalToast) => toast.error(title, options),
    /** Takes a toast raised with `options.id` away early, once what it said no longer holds. */
    dismiss: (id: string | number) => toast.dismiss(id),
};

// eslint-disable-next-line react-refresh/only-export-components
export { Notice, notify, type NoticeProps };
